import { useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Counts up to `value`. With `animate={false}` the value is shown as is, with
// no count-up now or on later changes (figures restored from a cache).
export default function AnimatedNumber({
  value,
  duration = 600,
  format = (n) => Math.round(n).toLocaleString("en-US"),
  startOnMount = true,
  animate = true,
}) {
  const target = Number(value) || 0;
  const initial = startOnMount && animate ? 0 : target;
  const [display, setDisplay] = useState(initial);
  const fromRef = useRef(initial);
  const frameRef = useRef(null);

  useEffect(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    const from = fromRef.current;
    const to = target;
    if (!animate || from === to) {
      fromRef.current = to;
      setDisplay(to);
      return undefined;
    }
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
        frameRef.current = null;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, animate]);

  return <>{format(animate ? display : target)}</>;
}
